import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { Grid, Button, Tooltip } from "@material-ui/core";
import { AddIcon, DeleteIcon, EditIcon } from "@icarius-icons";
import { getAppColor } from "@icarius-app/selectors";
import AgGridContainer from "@icarius-common/commonPage/agGridContainer";
import ConfirmationDialog from "../confirmationDialog";
import ParameterFormDialog from "../parameterFormDialog";
import { getColumnDefByPage } from "@icarius-table/columnDefs";

const ParametersSection = ({ parameters, onChange, hasGenerated, requiredTypes, listTypes, paramTypes }) => {
    const dispatch = useDispatch();
    const color = useSelector(getAppColor);

    const parametersGridRef = useRef(null);
    const [parameterDialogState, setParameterDialogState] = useState({ open: false, index: null, data: null });
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    const getNextParamNum = () =>
        (parameters || []).reduce((max, parameter) => Math.max(max, Number(parameter.paramNum) || 0), 0) + 1;

    const handleOpenParameterDialog = (parameterData = null, index = null) => {
        setParameterDialogState({ open: true, index, data: parameterData });
    };

    const handleCloseParameterDialog = () => {
        setParameterDialogState({ open: false, index: null, data: null });
    };

    const handleSaveParameter = (parameterData) => {
        const newParameters = [...(parameters || [])];

        const enrichedData = {
            ...parameterData,
            paramTypeString:
                paramTypes?.find((item) => item.key === parameterData.paramType)?.value ||
                parameterData.paramTypeString ||
                "",
            listTypeString:
                listTypes?.find((item) => item.key === parameterData.listType)?.value ||
                parameterData.listTypeString ||
                "",
            requireString:
                requiredTypes?.find((item) => item.key === parameterData.required)?.value ||
                parameterData.requireString ||
                "",
        };

        if (parameterDialogState.index !== null) {
            newParameters[parameterDialogState.index] = enrichedData;
        } else {
            newParameters.push(enrichedData);
        }
        onChange(newParameters);
        handleCloseParameterDialog();
    };

    const handleOpenConfirmDialog = () => {
        const selectedNodes = parametersGridRef.current?.api?.getSelectedNodes();
        if (!selectedNodes || selectedNodes.length === 0) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectSingleItem"), severity: "warning" }));
            return;
        }
        setOpenConfirmDialog(true);
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
    };

    const handleDeleteSelectedParameters = () => {
        if (parametersGridRef.current?.api) {
            const selectedNodes = parametersGridRef.current.api.getSelectedNodes();
            const selectedIndexes = selectedNodes.map((node) => parseInt(node.id));
            const newParameters = (parameters || []).filter((_, index) => !selectedIndexes.includes(index));
            onChange(newParameters);
            parametersGridRef.current.api.deselectAll();
        }
        handleCloseConfirmDialog();
    };

    const handleEditSelectedParameter = () => {
        const selectedNodes = parametersGridRef.current?.api?.getSelectedNodes();
        if (!selectedNodes || selectedNodes.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectSingleItem"), severity: "warning" }));
            return;
        }
        const node = selectedNodes[0];
        handleOpenParameterDialog(node.data, parseInt(node.id));
    };

    return (
        <>
            <Grid container item alignItems="center" xs={12} style={{ padding: "8px 10px", marginTop: 24 }}>
                {" "}
                {/* parámetros */}
                <Grid
                    item
                    xs={12}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}
                >
                    <h3 style={{ margin: 0, color: "var(--mainText)", fontWeight: "normal", fontSize: "1rem" }}>
                        {getLocalizedString("queryParameters")}
                    </h3>
                    <div style={{ display: "flex", gap: 10 }}>
                        <Tooltip title={getLocalizedString("add")}>
                            <Button
                                variant={"outlined"}
                                disabled={!hasGenerated}
                                onClick={() => handleOpenParameterDialog()}
                            >
                                <AddIcon htmlColor={color} />
                            </Button>
                        </Tooltip>
                        <Tooltip title={getLocalizedString("modify")}>
                            <Button variant={"outlined"} disabled={!hasGenerated} onClick={handleEditSelectedParameter}>
                                <EditIcon htmlColor={color} />
                            </Button>
                        </Tooltip>
                        <Tooltip title={getLocalizedString("delete")}>
                            <Button variant={"outlined"} disabled={!hasGenerated} onClick={handleOpenConfirmDialog}>
                                <DeleteIcon htmlColor={color} />
                            </Button>
                        </Tooltip>
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <AgGridContainer
                        ref={parametersGridRef}
                        ownColumnDef={getColumnDefByPage("queryDefinitionAiParameters")}
                        rowData={parameters || []}
                        gridTitle={getLocalizedString("queryParameters")}
                        height={220}
                        hasFixedFilters={false}
                    />
                </Grid>
            </Grid>
            <ConfirmationDialog
                open={openConfirmDialog}
                handleClose={handleCloseConfirmDialog}
                handleConfirm={handleDeleteSelectedParameters}
                title={getLocalizedString("delete")}
                text={getLocalizedString("areYouSureYouWantToDeleteTheParameter")}
            />
            <ParameterFormDialog
                open={parameterDialogState.open}
                handleClose={handleCloseParameterDialog}
                handleSave={handleSaveParameter}
                initialData={parameterDialogState.data}
                paramNumToUse={getNextParamNum()}
                requiredTypes={requiredTypes}
                listTypes={listTypes}
                paramTypes={paramTypes}
            />
        </>
    );
};

export default ParametersSection;
